import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import { API_BASE } from "../../config/api";
import toast from "react-hot-toast";
import { ThreeDots } from "react-loader-spinner";
import "./Galerie.css";

// Vocabulaires proposes (aides de saisie cote UI — PAS des regles de decision).
const ETAPES = [
  "Diagnostic initial", "Bilan pré-thérapeutique", "Chimiothérapie (cure)",
  "Chirurgie / post-opératoire", "Radiothérapie", "Évaluation de réponse",
  "Rémission", "Rechute", "Soins de support", "Suivi post-traitement", "Autre",
];
const MOTIFS = [
  "Suivi évolution tumorale", "Documentation clinique", "Comparaison avant/après",
  "Cicatrisation post-chirurgicale", "Effet indésirable / toxicité",
  "Campagne de sensibilisation / financement", "Autre",
];
const LOCALISATIONS = [
  "Tête / cou", "Orbite", "Thorax", "Abdomen", "Pelvis",
  "Membre supérieur", "Membre inférieur", "Peau", "Autre",
];
const USAGES = [
  "Soins uniquement", "Usage pédagogique interne",
  "Campagne de financement (consentement requis)", "Recherche",
];
const ETAPE_RANK = ETAPES.reduce((a, e, i) => { a[e] = i; return a; }, {});
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const needsConsent = (usage) => (usage || "").toLowerCase().includes("campagne");
// Consentement EFFECTIF = consenti ET non retire (revocable, medico-legal).
const consentEff = (o) => !!(o && o.consentement_parental) && !(o && o.retrait_consentement);

// Vidéos : limite alignée au backend (2 Go) + détection (type MIME ou extension de secours).
const GAL_VIDEO_MAX = 2 * 1024 * 1024 * 1024;
const isVideoFile = (f) => !!(f && (
  (f.type && f.type.startsWith("video/")) ||
  /\.(mp4|mov|m4v|webm|avi|mkv|3gp|3gpp|mpeg|mpg|ogv|wmv|flv|ts)$/i.test(f.name || "")
));

// Downscale cote client en respectant l'orientation EXIF (fallback: fichier original).
async function downscale(file, maxDim = 2000, quality = 0.85) {
  try {
    if (!file.type || !file.type.startsWith("image/")) return file;
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const { width, height } = bmp;
    if (Math.max(width, height) <= maxDim) { if (bmp.close) bmp.close(); return file; }
    const scale = maxDim / Math.max(width, height);
    const w = Math.round(width * scale), h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    if (bmp.close) bmp.close();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }
}

const FieldSelect = ({ label, value, onChange, options, placeholder }) => (
  <label className="gal-field">
    <span className="gal-field-label">{label}</span>
    <select className="gal-input" value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="">{placeholder || "—"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const Galerie = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || (list || []).find((d) => String(d.id) === String(id)) || {};
  const nav = useRubriqueNav("galerie");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lbId, setLbId] = useState(null);                 // identité de la photo ouverte (≠ index)
  const [editing, setEditing] = useState(null);
  const [tray, setTray] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState(null);         // {done,total}
  const [vpct, setVpct] = useState(null);                 // progression octet de la vidéo en cours (%)
  const [filterEtape, setFilterEtape] = useState("");
  const [filterUsage, setFilterUsage] = useState("");
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const busy = useRef(false);                              // verrou synchrone anti double-soumission
  const trayRef = useRef([]);

  const base = `${API_BASE}/api/dossiers/${id}/galerie`;
  // Upload vidéo : route DIRECTE vers api_vps :5004 (bypass du tunnel Cloudflare qui plafonne
  // le corps a ~100 Mo). En https on passe par /galerie-video/ ; en dev http on retombe sur le tunnel.
  const videoUrl = (typeof window !== "undefined" && window.location && window.location.protocol === "https:")
    ? `${window.location.origin}/galerie-video/api/dossiers/${id}/galerie/video`
    : `${base}/video`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(base);
      if (!r.ok) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (Array.isArray(j.data)) setItems(j.data);        // sur erreur: on NE vide PAS items
    } catch (e) {
      toast.error("Impossible de charger la galerie");
    } finally { setLoading(false); }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  // Nettoyage des Object URLs au démontage (évite les fuites de blobs).
  useEffect(() => { trayRef.current = tray; }, [tray]);
  useEffect(() => () => { trayRef.current.forEach((it) => URL.revokeObjectURL(it.preview)); }, []);

  // ── Filtrage + regroupement par etape (chronologique) ────────────
  const filtered = useMemo(() => {
    let a = items.slice();
    if (filterEtape) a = a.filter((x) => (x.etape_traitement || "") === filterEtape);
    if (filterUsage) a = a.filter((x) => (x.usage_autorise || "") === filterUsage);
    return a;
  }, [items, filterEtape, filterUsage]);

  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((x) => {
      const k = x.etape_traitement || "Non précisé";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(x);
    });
    const arr = Array.from(m.entries());
    arr.forEach(([, v]) => v.sort((p, q) =>
      String(p.date_prise || p.uploaded_at || "").localeCompare(String(q.date_prise || q.uploaded_at || ""))));
    arr.sort((p, q) => (ETAPE_RANK[p[0]] ?? 99) - (ETAPE_RANK[q[0]] ?? 99));
    return arr;
  }, [filtered]);

  const etapesPresentes = useMemo(
    () => Array.from(new Set(items.map((x) => x.etape_traitement).filter(Boolean))), [items]);
  const usagesPresents = useMemo(
    () => Array.from(new Set(items.map((x) => x.usage_autorise).filter(Boolean))), [items]);

  // ── Selection de fichiers -> plateau d'upload ────────────────────
  const addFiles = useCallback(async (fileList) => {
    if (busy.current) return;
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || isVideoFile(f));
    if (!files.length) { toast.error("Aucune image ou vidéo valide sélectionnée"); return; }
    const tooBig = files.filter((f) => isVideoFile(f) && f.size > GAL_VIDEO_MAX);
    if (tooBig.length) toast.error(`${tooBig.length} vidéo(s) > 2 Go ignorée(s)`);
    const kept = files.filter((f) => !(isVideoFile(f) && f.size > GAL_VIDEO_MAX));
    if (!kept.length) return;
    setPreparing(true);
    try {
      const prepared = await Promise.all(kept.map(async (f) => {
        const vid = isVideoFile(f);
        const small = vid ? f : await downscale(f);   // pas de downscale pour la vidéo
        return {
          key: Math.random().toString(36).slice(2),
          file: small,
          isVideo: vid,
          preview: URL.createObjectURL(small),
          meta: {
            motif: "", etape_traitement: "", date_prise: todayISO(), localisation_anatomique: "",
            usage_autorise: "Soins uniquement", consentement_parental: false,
            observation: "", commentaire: "",
          },
        };
      }));
      setTray((t) => [...t, ...prepared]);
    } finally { setPreparing(false); }
  }, []);

  const onFileInput = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); if (!drag) setDrag(true); };
  const onDragLeave = () => setDrag(false);

  const setTrayMeta = (key, patch) =>
    setTray((t) => t.map((it) => (it.key === key ? { ...it, meta: { ...it.meta, ...patch } } : it)));
  const applyToAll = (patch) =>
    setTray((t) => t.map((it) => ({ ...it, meta: { ...it.meta, ...patch } })));
  const removeTray = (key) =>
    setTray((t) => { const it = t.find((x) => x.key === key); if (it) URL.revokeObjectURL(it.preview); return t.filter((x) => x.key !== key); });

  const trayInvalid = (it) => needsConsent(it.meta.usage_autorise) && !it.meta.consentement_parental;
  const hasInvalid = tray.some(trayInvalid);

  // Métadonnées -> FormData (booleans en "true"/"false", champs vides ignorés).
  const appendMeta = (fd, meta) =>
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) fd.append(k, typeof v === "boolean" ? (v ? "true" : "false") : v);
    });

  const uploadImage = async (it) => {
    const fd = new FormData();
    fd.append("image", it.file);
    appendMeta(fd, it.meta);
    const r = await fetch(base, { method: "POST", body: fd });
    if (!r.ok) {
      toast.error(r.status === 413 ? "Image trop volumineuse" : r.status === 422 ? "Consentement requis" : "Échec d'un envoi");
      return false;
    }
    const j = await r.json();
    if (!j.success) { toast.error(j.message || "Échec d'un envoi"); return false; }
    return true;
  };

  // Vidéo : XHR (et non fetch) pour la progression d'upload octet — indispensable sur des gros films.
  const uploadVideo = (it) => new Promise((resolve) => {
    const fd = new FormData();
    fd.append("video", it.file);
    appendMeta(fd, it.meta);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", videoUrl);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setVpct(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let j = {}; try { j = JSON.parse(xhr.responseText || "{}"); } catch (_) {}
      if (xhr.status >= 200 && xhr.status < 300 && j.success) resolve(true);
      else {
        toast.error(xhr.status === 413 ? "Vidéo trop volumineuse (> 2 Go)"
          : xhr.status === 422 ? "Consentement requis"
          : (j.message || `Échec de l'envoi vidéo (${xhr.status || "réseau"})`));
        resolve(false);
      }
    };
    xhr.onerror = () => { toast.error("Échec réseau (vidéo)"); resolve(false); };
    xhr.send(fd);
  });

  const uploadAll = async () => {
    if (busy.current) return;
    if (hasInvalid) {
      toast.error("Consentement parental requis pour les médias « campagne » signalés");
      return;
    }
    busy.current = true;
    setUploading(true);
    setProgress({ done: 0, total: tray.length });
    const failed = [];
    let ok = 0;
    for (let i = 0; i < tray.length; i++) {
      const it = tray[i];
      try {
        const okItem = it.isVideo ? await uploadVideo(it) : await uploadImage(it);
        if (okItem) { ok++; URL.revokeObjectURL(it.preview); }
        else failed.push(it);
      } catch (e) { failed.push(it); }
      setProgress({ done: i + 1, total: tray.length });
      setVpct(null);
    }
    setTray(failed);
    setUploading(false);
    setProgress(null);
    setVpct(null);
    busy.current = false;
    if (ok) toast.success(`${ok} média${ok > 1 ? "s" : ""} ajouté${ok > 1 ? "s" : ""}`);
    if (failed.length) toast.error(`${failed.length} en échec — corrigez puis réessayez`);
    load();
  };

  const cancelTray = () => { tray.forEach((it) => URL.revokeObjectURL(it.preview)); setTray([]); };

  // ── Edition / suppression ────────────────────────────────────────
  const saveEdit = async () => {
    if (!editing || busy.current) return;
    if (needsConsent(editing.usage_autorise) && !editing.consentement_parental) {
      toast.error("Consentement parental requis pour un usage « campagne »");
      return;
    }
    busy.current = true;
    try {
      const r = await fetch(`${API_BASE}/api/galerie/${editing.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          motif: editing.motif, etape_traitement: editing.etape_traitement,
          date_prise: editing.date_prise, localisation_anatomique: editing.localisation_anatomique,
          usage_autorise: editing.usage_autorise, consentement_parental: !!editing.consentement_parental,
          consentement_par: editing.consentement_par || "",
          // Re-cocher le consentement leve un eventuel retrait anterieur.
          retrait_consentement: editing.consentement_parental ? false : !!editing.retrait_consentement,
          observation: editing.observation, commentaire: editing.commentaire,
        }),
      });
      if (!r.ok && r.status !== 422) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (j.success) { toast.success("Métadonnées mises à jour"); setEditing(null); load(); }
      else toast.error(j.message || "Échec de la mise à jour");
    } catch (e) { toast.error("Échec réseau"); }
    finally { busy.current = false; }
  };

  // Retrait du consentement (revocation) : patch DEDIE {retrait_consentement:true} — toujours enregistrable.
  const retractConsent = async (img) => {
    if (busy.current) return;
    if (!window.confirm("Retirer le consentement ? La photo ne pourra plus être utilisée en diffusion (campagne).")) return;
    busy.current = true;
    try {
      const r = await fetch(`${API_BASE}/api/galerie/${img.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ retrait_consentement: true }),
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (j.success) { toast.success("Consentement retiré"); setEditing(null); load(); }
      else toast.error(j.message || "Échec");
    } catch (e) { toast.error("Échec réseau"); }
    finally { busy.current = false; }
  };

  const removeImage = async (img) => {
    if (busy.current) return;
    if (!window.confirm("Supprimer définitivement cette photo ?")) return;
    busy.current = true;
    try {
      const r = await fetch(`${API_BASE}/api/galerie/${img.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (j.success) {
        toast.success("Photo supprimée");
        setLbId(null);
        setItems((prev) => prev.filter((x) => x.id !== img.id));
      } else toast.error(j.message || "Échec de la suppression");
    } catch (e) { toast.error("Échec réseau"); }
    finally { busy.current = false; }
  };

  // ── Navigation lightbox (par identité) ───────────────────────────
  const closeLb = useCallback(() => setLbId(null), []);
  const stepLb = useCallback((d) => {
    const n = filtered.length; if (!n) return;
    const i = filtered.findIndex((x) => x.id === lbId);
    if (i < 0) return;
    setLbId(filtered[(i + d + n) % n].id);
  }, [filtered, lbId]);

  useEffect(() => {
    if (lbId == null) return;
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag || "")) {
        if (e.key === "Escape" && editing) setEditing(null);
        return;
      }
      if (editing) { if (e.key === "Escape") setEditing(null); return; }
      if (e.key === "Escape") closeLb();
      else if (e.key === "ArrowRight") stepLb(1);
      else if (e.key === "ArrowLeft") stepLb(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lbId, editing, closeLb, stepLb]);

  const touch = useRef({ x: 0 });
  const onTouchStart = (e) => { touch.current.x = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 50) stepLb(dx < 0 ? 1 : -1);
  };

  const cur = useMemo(() => (lbId == null ? null : filtered.find((x) => x.id === lbId) || null), [filtered, lbId]);
  const curPos = useMemo(() => (lbId == null ? -1 : filtered.findIndex((x) => x.id === lbId)), [filtered, lbId]);

  return (
    <div className="gal-page">
      <header className="gal-header">
        <div className="gal-header-left">
          <button className="gal-back" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="gal-title">Galerie — Dossier {dossier.numero_dossier || dossier.id || id}</h1>
            <p className="gal-subtitle">
              {dossier.patient ? <>Patient : {dossier.patient} &nbsp;|&nbsp; </> : null}
              {items.length} photo{items.length > 1 ? "s" : ""} de suivi
            </p>
          </div>
        </div>
        <button className="gal-add-btn" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading || preparing}>
          {preparing ? "Préparation…" : "+ Ajouter photos / vidéos"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={onFileInput} disabled={uploading} />
      </header>

      {/* Plateau d'upload */}
      {tray.length > 0 && (
        <section className="gal-tray">
          <div className="gal-tray-head">
            <strong>{tray.length} fichier{tray.length > 1 ? "s" : ""} à téléverser</strong>
            <div className="gal-tray-batch">
              <span className="gal-batch-hint">Appliquer à toutes :</span>
              <select className="gal-input gal-input--sm" aria-label="Étape pour toutes" onChange={(e) => e.target.value && applyToAll({ etape_traitement: e.target.value })} defaultValue="">
                <option value="">Étape…</option>
                {ETAPES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <select className="gal-input gal-input--sm" aria-label="Usage pour toutes" onChange={(e) => e.target.value && applyToAll({ usage_autorise: e.target.value })} defaultValue="">
                <option value="">Usage…</option>
                {USAGES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <input className="gal-input gal-input--sm" type="date" aria-label="Date pour toutes" onChange={(e) => e.target.value && applyToAll({ date_prise: e.target.value })} />
            </div>
          </div>
          <div className="gal-tray-grid">
            {tray.map((it) => (
              <div className={"gal-tray-card" + (trayInvalid(it) ? " gal-tray-card--invalid" : "")} key={it.key}>
                <div className="gal-tray-thumb" style={it.isVideo ? undefined : { backgroundImage: `url(${it.preview})` }}>
                  {it.isVideo && <video className="gal-tray-video" src={it.preview} muted playsInline preload="metadata" />}
                  {it.isVideo && <span className="gal-tray-badge">🎬 vidéo</span>}
                  <button className="gal-tray-del" title="Retirer" aria-label="Retirer" onClick={() => removeTray(it.key)}>×</button>
                </div>
                <FieldSelect label="Motif" value={it.meta.motif} onChange={(v) => setTrayMeta(it.key, { motif: v })} options={MOTIFS} placeholder="Motif…" />
                <FieldSelect label="Étape / période" value={it.meta.etape_traitement} onChange={(v) => setTrayMeta(it.key, { etape_traitement: v })} options={ETAPES} placeholder="Étape…" />
                <label className="gal-field"><span className="gal-field-label">Date de prise</span>
                  <input className="gal-input" type="date" value={it.meta.date_prise} onChange={(e) => setTrayMeta(it.key, { date_prise: e.target.value })} /></label>
                <FieldSelect label="Localisation" value={it.meta.localisation_anatomique} onChange={(v) => setTrayMeta(it.key, { localisation_anatomique: v })} options={LOCALISATIONS} placeholder="Localisation…" />
                <FieldSelect label="Usage autorisé" value={it.meta.usage_autorise} onChange={(v) => setTrayMeta(it.key, { usage_autorise: v })} options={USAGES} placeholder="Usage…" />
                {needsConsent(it.meta.usage_autorise) && (<>
                  <label className={"gal-consent" + (trayInvalid(it) ? " gal-consent--req" : "")}>
                    <input type="checkbox" checked={it.meta.consentement_parental} onChange={(e) => setTrayMeta(it.key, { consentement_parental: e.target.checked })} />
                    <span>Consentement parental obtenu</span>
                  </label>
                  {it.meta.consentement_parental && (
                    <input className="gal-input gal-input--sm" placeholder="Obtenu de (parent / tuteur)" value={it.meta.consentement_par || ""} onChange={(e) => setTrayMeta(it.key, { consentement_par: e.target.value })} />
                  )}
                </>)}
                <label className="gal-field"><span className="gal-field-label">Observation</span>
                  <textarea className="gal-input gal-textarea" rows={2} value={it.meta.observation} onChange={(e) => setTrayMeta(it.key, { observation: e.target.value })} placeholder="Observation clinique…" /></label>
              </div>
            ))}
          </div>
          <div className="gal-tray-actions">
            <button className="gal-btn-ghost" onClick={cancelTray} disabled={uploading}>Annuler</button>
            <button className="gal-btn-primary" onClick={uploadAll} disabled={uploading || hasInvalid}>
              {uploading
                ? `Envoi… ${progress ? progress.done + "/" + progress.total : ""}${vpct != null ? ` · vidéo ${vpct}%` : ""}`
                : `Téléverser ${tray.length} fichier${tray.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </section>
      )}

      {/* Filtres */}
      {items.length > 0 && (
        <div className="gal-filters">
          <select className="gal-input gal-input--sm" aria-label="Filtrer par étape" value={filterEtape} onChange={(e) => setFilterEtape(e.target.value)}>
            <option value="">Toutes les étapes</option>
            {etapesPresentes.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select className="gal-input gal-input--sm" aria-label="Filtrer par usage" value={filterUsage} onChange={(e) => setFilterUsage(e.target.value)}>
            <option value="">Tous les usages</option>
            {usagesPresents.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {(filterEtape || filterUsage) && (
            <button className="gal-btn-ghost gal-btn-ghost--sm" onClick={() => { setFilterEtape(""); setFilterUsage(""); }}>Réinitialiser</button>
          )}
          <span className="gal-filter-count">{filtered.length} / {items.length}</span>
        </div>
      )}

      {/* Contenu (drop actif aussi sur galerie peuplée) */}
      <main className="gal-body" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} data-drag={drag ? "1" : "0"}>
        {drag && items.length > 0 && <div className="gal-drophint">Déposez pour ajouter des photos</div>}
        {loading ? (
          <div className="gal-loading"><ThreeDots height="46" width="46" color="#2563eb" visible /></div>
        ) : items.length === 0 ? (
          <div className="gal-empty" data-drag={drag ? "1" : "0"}>
            <div className="gal-empty-icon">🖼️</div>
            <h3>Aucune photo dans ce dossier</h3>
            <p>Documentez l'évolution clinique du patient : glissez des images ici ou cliquez sur « Ajouter des photos ».</p>
            <button className="gal-btn-primary" onClick={() => fileRef.current && fileRef.current.click()} disabled={preparing}>
              {preparing ? "Préparation…" : "Ajouter la première photo"}
            </button>
          </div>
        ) : (
          groups.map(([etape, imgs]) => (
            <section className="gal-group" key={etape}>
              <div className="gal-group-head">
                <span className="gal-group-dot" />
                <h2 className="gal-group-title">{etape}</h2>
                <span className="gal-group-count">{imgs.length}</span>
              </div>
              <div className="gal-grid">
                {imgs.map((img) => (
                  <button className="gal-cell" key={img.id} onClick={() => setLbId(img.id)} title={img.motif || ""} aria-label={"Ouvrir " + (img.kind === "video" ? "la vidéo" : "la photo") + (img.motif ? " : " + img.motif : "")}>
                    {img.kind === "video" ? (
                      <div className="gal-thumb gal-thumb--video">
                        <video className="gal-thumb-vid" preload="metadata" muted playsInline src={`${API_BASE}${img.url}#t=0.1`} />
                        <span className="gal-play-badge" aria-hidden="true">▶</span>
                      </div>
                    ) : (
                      <img className="gal-thumb" loading="lazy" alt={img.motif || "photo"} src={`${API_BASE}${img.thumb_url}`} />
                    )}
                    <div className="gal-cell-meta">
                      {img.kind === "video" && <span className="gal-chip">🎬 vidéo</span>}
                      {img.date_prise && <span className="gal-chip">{img.date_prise}</span>}
                      {needsConsent(img.usage_autorise) && <span className="gal-chip gal-chip--warn" title="Usage campagne">📣</span>}
                    </div>
                    {img.motif && <div className="gal-cell-motif">{img.motif}</div>}
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      {/* Lightbox */}
      {cur && (
        <div className="gal-lb" role="dialog" aria-modal="true" aria-label="Photo du dossier" onClick={closeLb}>
          <button className="gal-lb-close" onClick={closeLb} aria-label="Fermer">×</button>
          {filtered.length > 1 && <button className="gal-lb-nav gal-lb-prev" onClick={(e) => { e.stopPropagation(); stepLb(-1); }} aria-label="Photo précédente">‹</button>}
          <div className="gal-lb-stage" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="gal-lb-imgwrap">
              {cur.kind === "video" ? (
                <video className="gal-lb-img" controls autoPlay playsInline src={`${API_BASE}${cur.url}`} />
              ) : (
                <img className="gal-lb-img" alt={cur.motif || "photo"} src={`${API_BASE}${cur.url}`} />
              )}
              <span className="gal-lb-counter">{curPos + 1} / {filtered.length}</span>
            </div>
            <aside className="gal-lb-panel">
              {editing && editing.id === cur.id ? (
                <div className="gal-edit">
                  <h3>Modifier les informations</h3>
                  <FieldSelect label="Motif" value={editing.motif} onChange={(v) => setEditing({ ...editing, motif: v })} options={MOTIFS} placeholder="Motif…" />
                  <FieldSelect label="Étape / période" value={editing.etape_traitement} onChange={(v) => setEditing({ ...editing, etape_traitement: v })} options={ETAPES} placeholder="Étape…" />
                  <label className="gal-field"><span className="gal-field-label">Date de prise</span>
                    <input className="gal-input" type="date" value={editing.date_prise || ""} onChange={(e) => setEditing({ ...editing, date_prise: e.target.value })} /></label>
                  <FieldSelect label="Localisation" value={editing.localisation_anatomique} onChange={(v) => setEditing({ ...editing, localisation_anatomique: v })} options={LOCALISATIONS} placeholder="Localisation…" />
                  <FieldSelect label="Usage autorisé" value={editing.usage_autorise} onChange={(v) => setEditing({ ...editing, usage_autorise: v })} options={USAGES} placeholder="Usage…" />
                  {needsConsent(editing.usage_autorise) && (<>
                    <label className={"gal-consent" + (!editing.consentement_parental ? " gal-consent--req" : "")}><input type="checkbox" checked={!!editing.consentement_parental} onChange={(e) => setEditing({ ...editing, consentement_parental: e.target.checked })} /><span>Consentement parental obtenu</span></label>
                    {editing.consentement_parental && (
                      <input className="gal-input" placeholder="Obtenu de (parent / tuteur)" value={editing.consentement_par || ""} onChange={(e) => setEditing({ ...editing, consentement_par: e.target.value })} />
                    )}
                    {consentEff(cur) && (
                      <button className="gal-btn-danger gal-btn-ghost--sm" type="button" onClick={() => retractConsent(cur)}>Retirer le consentement</button>
                    )}
                  </>)}
                  <label className="gal-field"><span className="gal-field-label">Observation</span>
                    <textarea className="gal-input gal-textarea" rows={2} value={editing.observation || ""} onChange={(e) => setEditing({ ...editing, observation: e.target.value })} /></label>
                  <label className="gal-field"><span className="gal-field-label">Commentaire</span>
                    <textarea className="gal-input gal-textarea" rows={2} value={editing.commentaire || ""} onChange={(e) => setEditing({ ...editing, commentaire: e.target.value })} /></label>
                  <div className="gal-edit-actions">
                    <button className="gal-btn-ghost" onClick={() => setEditing(null)}>Annuler</button>
                    <button className="gal-btn-primary" onClick={saveEdit}>Enregistrer</button>
                  </div>
                </div>
              ) : (
                <div className="gal-info">
                  <h3>{cur.motif || (cur.kind === "video" ? "Vidéo de suivi" : "Photo de suivi")}</h3>
                  <dl className="gal-dl">
                    {cur.etape_traitement && <><dt>Étape</dt><dd>{cur.etape_traitement}</dd></>}
                    {cur.date_prise && <><dt>Date de prise</dt><dd>{cur.date_prise}</dd></>}
                    {cur.localisation_anatomique && <><dt>Localisation</dt><dd>{cur.localisation_anatomique}</dd></>}
                    {cur.usage_autorise && <><dt>Usage</dt><dd>{cur.usage_autorise}</dd></>}
                    {needsConsent(cur.usage_autorise) && <><dt>Consentement</dt><dd>{
                      cur.retrait_consentement
                        ? `Retiré${cur.date_retrait ? ` le ${cur.date_retrait}` : ""} — non diffusable ⚠️`
                        : cur.consentement_parental
                          ? `Obtenu${cur.date_consentement ? ` le ${cur.date_consentement}` : ""}${cur.consentement_par ? ` (${cur.consentement_par})` : ""} ✓`
                          : "Manquant ⚠️"
                    }</dd></>}
                    {cur.observation && <><dt>Observation</dt><dd>{cur.observation}</dd></>}
                    {cur.commentaire && <><dt>Commentaire</dt><dd>{cur.commentaire}</dd></>}
                    {cur.uploaded_by && <><dt>Ajoutée par</dt><dd>{cur.uploaded_by}</dd></>}
                    {cur.largeur && cur.hauteur && <><dt>Dimensions</dt><dd>{cur.largeur}×{cur.hauteur}</dd></>}
                  </dl>
                  <div className="gal-info-actions">
                    <a className="gal-btn-ghost" href={`${API_BASE}${cur.url}`} target="_blank" rel="noreferrer">Ouvrir</a>
                    <button className="gal-btn-ghost" onClick={() => setEditing({ ...cur })}>Modifier</button>
                    <button className="gal-btn-danger" onClick={() => removeImage(cur)}>Supprimer</button>
                  </div>
                </div>
              )}
            </aside>
          </div>
          {filtered.length > 1 && <button className="gal-lb-nav gal-lb-next" onClick={(e) => { e.stopPropagation(); stepLb(1); }} aria-label="Photo suivante">›</button>}
        </div>
      )}

      <RubriqueNav goNext={nav.goNext} goPrev={nav.goPrev} goTo={nav.goTo} suivante={nav.suivante} precedente={nav.precedente} indexCourant={nav.indexCourant} total={nav.total} />
    </div>
  );
};

export default Galerie;
